import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const ClusterPage = () => {
  const navigate = useNavigate();
  const [cluster, setCluster] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    country: localStorage.getItem("defaultCountry"), // Default India
    state: localStorage.getItem("defaultState"),   // Default Gujarat
    city: localStorage.getItem("defaultCity"), 
    address: '',
    phoneNumber: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);
  // Fetch dropdowns for country, state, city
 useEffect(() => {
  const fetchCountries = async () => {
    try {
      const res = await axios.get('/admin/api/country');
      setCountryOptions(res.data?.countries?.map(c => ({ value: c._id, label: c.name })) || []);
    } catch (err) {/**/}
  };
  fetchCountries();
}, []);

useEffect(() => {
  if (formValues.country) {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`/admin/api/state?countryId=${formValues.country}`);
        setStateOptions(res.data?.states?.map(s => ({ value: s._id, label: s.name })) || []);
        if (!formValues.state) {
          setFormValues(prev => ({
            ...prev,
            state: localStorage.getItem('defaultState') || ''
          }));
        }
      } catch (err) {/**/}
    };
    fetchStates();
  }
}, [formValues.country]);

useEffect(() => {
  if (formValues.state) {
    const fetchCities = async () => {
      try {
        const res = await axios.get(`/admin/api/city?stateId=${formValues.state}`);
        setCityOptions(res.data?.cities?.map(c => ({ value: c._id, label: c.name })) || []);
        if (!formValues.city) {
          setFormValues(prev => ({
            ...prev,
            city: localStorage.getItem('defaultCity') || ''
          }));
        }
      } catch (err) {/**/}
    };
    fetchCities();
  }
}, [formValues.state]);

  // Debounce search query - only trigger when 3+ characters or empty
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 0) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchCluster(currentPage, debouncedSearchQuery);
  }, [currentPage, debouncedSearchQuery]);

  const fetchCluster = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`/admin/api/clusters/all?page=${page}&limit=${limit}&search=${search}`);
      const { cluster = [], totalCount = 0 } = res.data;

      setCluster(cluster);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Cluster:', err);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editingId) {
        await axios.put(`/admin/api/clusters/${editingId}`, formValues);
        toastSuccess('Cluster updated successfully!');
      } else {
        await axios.post('/admin/api/clusters', formValues);
        toastSuccess('Cluster added successfully!');
      }
      setCurrentPage(1);
      fetchCluster(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Cluster:', err);
      toastError('Failed to save Cluster');
    }
  };

 const handleEdit = async (cluster) => {
  // Get _id for country/state/city, fallback to default if missing
  const countryId = cluster.country?._id || cluster.country || localStorage.getItem("defaultCountry") || '';
  const stateId = cluster.state?._id || cluster.state || localStorage.getItem("defaultState") || '';
  const cityId = cluster.city?._id || cluster.city || localStorage.getItem("defaultCity") || '';

  setFormValues({
    name: cluster.name || '',
    country: countryId,
    state: stateId,
    city: cityId,
    address: cluster.address || '',
    phoneNumber: cluster.phoneNumber || '',
  });
  setEditingId(cluster._id);
  setShowModal(true);

  // Fetch states for country
  if (countryId) {
    try {
      const res = await axios.get(`/admin/api/state?countryId=${countryId}`);
      setStateOptions(res.data?.states?.map(s => ({ value: s._id, label: s.name })) || []);
    } catch (err) {/* handle error */}
  }

  // Fetch cities for state
  if (stateId) {
    try {
      const res = await axios.get(`/admin/api/city?stateId=${stateId}`);
      setCityOptions(res.data?.cities?.map(c => ({ value: c._id, label: c.name })) || []);
    } catch (err) {/* handle error */}
  }
};

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure you want to delete this cluster?');
    if (result?.isConfirmed) {
      try {
        await axios.delete(`/admin/api/clusters/${id}`);
        toastSuccess('Cluster deleted successfully!');
        setCurrentPage(1);
        fetchCluster(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Cluster:', err);
        toastError('Failed to delete Cluster');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ name: '',
      country: '',
      state: '',
      city: '', 
    });
    setEditingId(null);
    setErrors({});
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Cluster</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Cluster (min 3 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Cluster List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New Cluster
          </button>
        </div>
      <div className="table-responsive">
        <div className="card-body p-0">
          <table className="table table-bordered table-sm mb-0">
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Cluster Name</th>
                <th className="py-1 px-2">Address</th>
                <th className="py-1 px-2">Phone</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cluster.length > 0 ? (
                cluster.map((cluster, idx) => {
                  return (
                    <tr key={cluster._id}>
                      <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                      <td className="py-1 px-2">
                        <span
                          style={{ color: '#F94F01', cursor: 'pointer', fontWeight: 'bold' }}
                          onClick={() => navigate(`/chapter?clusterId=${cluster._id}`)}
                        >
                          {cluster.name}
                        </span>
                      </td>
                      <td className="py-1 px-2">{cluster.address}</td>
                      <td className="py-1 px-2">{cluster.phoneNumber}</td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                          title="Edit"
                          onClick={() => handleEdit(cluster)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                          onClick={() => handleDelete(cluster._id)}
                          title="Delete"
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-2">No Cluster found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>
              {/* Pagination numbers with ellipsis */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    acc.push('ellipsis');
                  }
                  acc.push(page);
                  return acc;
                }, [])
                .map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-1">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`btn btn-sm page-btn${currentPage === page ? ' active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  )
                )}
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit Cluster' : 'Add Cluster'}
        fields={[
          { name: 'name', label: 'Cluster', placeholder: 'Enter Cluster Name' },
          { name: 'address', label: 'Address', type: 'textarea', placeholder: 'Enter Address', rows: 2 },
          { name: 'country', label: 'Country', type: 'select', options: countryOptions, placeholder: 'Select Country' },
          { name: 'state', label: 'State', type: 'select', options: stateOptions, placeholder: 'Select State' },
          { name: 'city', label: 'City', type: 'select', options: cityOptions, placeholder: 'Select City' },
          { name: 'phoneNumber', label: 'Phone Number', type: 'text', placeholder: 'Enter Phone Number' },
        ]}
        values={formValues}
        errors={errors}
        onChange={setFormValues}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={editingId ? 'Update' : 'Save'}
        requiredFields={['name']}
      />
    </div>
  );
};

export default ClusterPage;
